<script setup lang="ts">
import { computed } from "vue";
import type { RenewalHistoryEntry } from "@/composables/useRenewalTracking";
import {
  Activity,
  ArrowRight,
  CalendarDays,
  Clock3,
  UserRound,
} from "lucide-vue-next";

interface ChangedValue {
  key: string;
  label: string;
  previous: string;
  current: string;
}

const props = withDefaults(
  defineProps<{
    history?: RenewalHistoryEntry[] | null;
    isLoading?: boolean;
    isUnavailable?: boolean;
  }>(),
  {
    history: null,
    isLoading: false,
    isUnavailable: false,
  }
);

const sortedHistory = computed(() => {
  return [...(props.history || [])].sort((left, right) => {
    const rightTime = parseTimestamp(right.created_at);
    const leftTime = parseTimestamp(left.created_at);

    if (rightTime === null && leftTime === null) return 0;
    if (rightTime === null) return -1;
    if (leftTime === null) return 1;
    return rightTime - leftTime;
  });
});

function parseTimestamp(value: string | null | undefined) {
  const timestamp = new Date(String(value || "")).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function formatAction(value: string | null | undefined) {
  return titleCase(value, "Renewal update");
}

function formatStatus(value: string | null | undefined) {
  return titleCase(value, "Not recorded");
}

function formatTimestamp(value: string | null | undefined) {
  const timestamp = parseTimestamp(value);
  if (timestamp === null) return "Date unavailable";

  return new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function actorLabel(entry: RenewalHistoryEntry) {
  if (entry.actor_name) return entry.actor_name;

  const actorType = entry.actor_type
    ? titleCase(entry.actor_type, "System")
    : "System";
  if (entry.actor_id != null) return `${actorType} #${entry.actor_id}`;

  return actorType;
}

function formatEffectiveDate(value: string | null | undefined) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      )
    : new Date(normalized);

  if (Number.isNaN(date.getTime())) return normalized;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatStatusTransition(entry: RenewalHistoryEntry) {
  const previous = entry.status_from ? formatStatus(entry.status_from) : "";
  const current = entry.status_to ? formatStatus(entry.status_to) : "";

  if (previous && current) return `${previous} → ${current}`;
  if (current) return `→ ${current}`;
  if (previous) return `${previous} →`;
  return "No status change";
}

function changedValues(entry: RenewalHistoryEntry): ChangedValue[] {
  const previousValues = isRecord(entry.previous_values)
    ? entry.previous_values
    : {};
  const newValues = isRecord(entry.new_values) ? entry.new_values : {};
  const keys = Array.from(
    new Set([...Object.keys(previousValues), ...Object.keys(newValues)])
  );

  return keys
    .filter(
      (key) =>
        serializeValue(previousValues[key]) !== serializeValue(newValues[key])
    )
    .map((key) => ({
      key,
      label: titleCase(key, key),
      previous: formatChangedValue(previousValues[key], key),
      current: formatChangedValue(newValues[key], key),
    }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function serializeValue(value: unknown) {
  return JSON.stringify(value ?? null);
}

function formatChangedValue(value: unknown, key: string) {
  if (value === null || value === undefined || value === "") return "Not set";

  if (typeof value === "string" && /date/i.test(key)) {
    return formatEffectiveDate(value) || value;
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function titleCase(value: string | null | undefined, fallback: string) {
  const source = String(value || "")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!source) return fallback;

  return source.replace(/\b\w/g, (character) => character.toUpperCase());
}
</script>

<template>
  <section
    data-testid="renewal-activity-log"
    class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
  >
    <div class="border-b border-slate-200 bg-gradient-to-r from-indigo-50/80 via-white to-white px-5 py-4">
      <div class="flex items-start justify-between gap-4">
        <div class="flex min-w-0 items-start gap-3">
          <div class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
            <Activity
              class="size-4"
              aria-hidden="true"
            />
          </div>
          <div class="min-w-0">
            <h2 class="text-sm font-bold text-slate-800">
              Renewal Activity
            </h2>
            <p class="mt-1 text-xs leading-5 text-slate-500">
              A chronological record of renewal updates and notes.
            </p>
          </div>
        </div>
        <span
          v-if="!isLoading && !isUnavailable"
          class="shrink-0 rounded-full bg-white px-2.5 py-1 text-[0.68rem] font-bold text-indigo-600 ring-1 ring-indigo-100"
        >
          {{ sortedHistory.length }} event{{ sortedHistory.length === 1 ? "" : "s" }}
        </span>
      </div>
    </div>

    <div
      v-if="isLoading"
      data-testid="renewal-activity-loading"
      class="space-y-3 p-5"
    >
      <div
        v-for="item in 3"
        :key="item"
        class="h-20 animate-pulse rounded-xl bg-slate-100"
      />
    </div>

    <p
      v-else-if="isUnavailable"
      data-testid="renewal-activity-unavailable"
      class="m-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
    >
      Renewal activity is unavailable right now.
    </p>

    <p
      v-else-if="!sortedHistory.length"
      data-testid="renewal-activity-empty"
      class="m-5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500"
    >
      No renewal activity has been recorded yet.
    </p>

    <ol
      v-else
      data-testid="renewal-activity-entries"
      class="divide-y divide-slate-100"
    >
      <li
        v-for="entry in sortedHistory"
        :key="entry.id || `${entry.action}-${entry.created_at}`"
        class="px-5 py-5 transition-colors hover:bg-slate-50/70"
      >
        <div class="flex items-start gap-3">
          <div class="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Activity
              class="size-4"
              aria-hidden="true"
            />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="text-sm font-bold text-slate-800">
                {{ formatAction(entry.action) }}
              </p>
              <span class="inline-flex w-fit rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100">
                {{ formatStatusTransition(entry) }}
              </span>
            </div>

            <div class="mt-2 flex flex-wrap items-center gap-2">
              <span class="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[0.68rem] font-medium text-slate-600">
                <Clock3
                  class="size-3.5 text-slate-400"
                  aria-hidden="true"
                />
                {{ formatTimestamp(entry.created_at) }}
              </span>
              <span class="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[0.68rem] font-medium text-slate-600">
                <UserRound
                  class="size-3.5 text-slate-400"
                  aria-hidden="true"
                />
                Updated by: {{ actorLabel(entry) }}
              </span>
              <span
                v-if="entry.effective_date"
                class="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[0.68rem] font-medium text-slate-600"
              >
                <CalendarDays
                  class="size-3.5 text-slate-400"
                  aria-hidden="true"
                />
                Effective {{ formatEffectiveDate(entry.effective_date) }}
              </span>
            </div>

            <p
              v-if="entry.note"
              class="mt-3 rounded-lg border-l-2 border-indigo-300 bg-indigo-50/60 px-3 py-2.5 text-sm leading-relaxed text-slate-600"
            >
              {{ entry.note }}
            </p>

            <div
              v-if="changedValues(entry).length"
              class="mt-4 border-t border-slate-100 pt-3"
            >
              <p class="text-[0.68rem] font-bold uppercase tracking-[0.12em] text-slate-400">
                Changed values
              </p>
              <dl class="mt-2 grid gap-2 sm:grid-cols-2">
                <div
                  v-for="change in changedValues(entry)"
                  :key="change.key"
                  class="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                >
                  <dt class="text-xs font-semibold text-slate-600">
                    {{ change.label }}
                  </dt>
                  <dd class="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs">
                    <span class="min-w-0 truncate rounded bg-slate-50 px-1.5 py-1 text-slate-400">
                      {{ change.previous }}
                    </span>
                    <ArrowRight
                      class="size-3.5 shrink-0 text-slate-300"
                      aria-hidden="true"
                    />
                    <span class="min-w-0 truncate rounded bg-emerald-50 px-1.5 py-1 font-semibold text-emerald-700">
                      {{ change.current }}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </li>
    </ol>
  </section>
</template>
